import { Add, Delete, Edit, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { IPaginationData } from '../../../api/base';
import userApi from '../../../api/user.api';
import ImageUploadWithPreview from '../../../components/ImageUpload';
import Table, { Order } from '../../../components/Table';
import UserRoleSelect from '../../../components/UserRoleSelect';
import { EUserRoles, IUser } from '../../../models/user.model';
import theme from '../../../theme';
import { LogoImage } from '../../styled';

export default function UserListPage() {
  const [dataSource, setDataSource] = useState<IPaginationData<IUser>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['created_at', 'desc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currItem, setCurrItem] = useState<Partial<IUser> | undefined>();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    userApi
      .getList({
        ...filter,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, orderBy, filter]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">User</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <div>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => {
                setCurrItem({ role: EUserRoles.Editor });
              }}
            >
              Create new
            </Button>
          </div>
        </div>
      </Box>

      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'photo_url',
            title: 'Photo',
            dataKey: 'photo_url',
            render: (data) =>
              data.picture ? (
                <LogoImage src={process.env.REACT_APP_API_URI + data.picture} />
              ) : null,
          },
          {
            key: 'email',
            title: 'Email',
            dataKey: 'email',
            sortable: false,
            render: (data) => <a href={`mailto:${data.email}`}>{data.email}</a>,
          },
          {
            key: 'name',
            title: 'Name',
            dataKey: 'name',
            sortable: true,
          },
          {
            key: 'lastname',
            title: 'Lastname',
            dataKey: 'lastName',
            sortable: false,
          },
          {
            key: 'organization',
            title: 'Organization',
            dataKey: 'organization',
            sortable: false,
          },
          {
            key: 'organizationRole',
            title: 'Organization Role',
            dataKey: 'organizationRole',
            sortable: false,
          },
          {
            key: 'phone',
            title: 'Phone',
            dataKey: 'phone',
            sortable: false,
          },
          {
            key: 'role',
            title: 'Role',
            dataKey: 'role',
            sortable: true,
          },
          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'subscribe_investor',
            title: 'Investor',
            dataKey: 'subscribe_investor',
            sortable: false,
            render: (data) => (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: data.subscribe_investor ? '#4caf50' : '#f44336',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {data.subscribe_investor ? '✓' : '✗'}
              </Box>
            ),
          },
          {
            key: 'subscribe_startup',
            title: 'Startup',
            dataKey: 'subscribe_startup',
            sortable: false,
            render: (data) => (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: data.subscribe_startup ? '#4caf50' : '#f44336',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {data.subscribe_startup ? '✓' : '✗'}
              </Box>
            ),
          },
          {
            key: 'subscribe_researcher',
            title: 'Researcher',
            dataKey: 'subscribe_researcher',
            sortable: false,
            render: (data) => (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: data.subscribe_researcher ? '#4caf50' : '#f44336',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {data.subscribe_researcher ? '✓' : '✗'}
              </Box>
            ),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <IconButton onClick={() => setCurrItem(data)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                userApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete user successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!currItem} onClose={() => setCurrDeletedId(undefined)}>
        <DialogTitle>
          {currItem?.id ? 'Update user' : 'Create new user'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" p={1}>
                Photo
              </Typography>
              <ImageUploadWithPreview
                style={{ width: 256 }}
                value={currItem?.picture}
                onChange={(value) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      picture: value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                size="small"
                name="email"
                required
                fullWidth
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.email}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      email: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Name"
                required
                name="name"
                size="small"
                type="text"
                fullWidth
                inputProps={{
                  min: 1,
                }}
                value={currItem?.name}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      name: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Lastname"
                required
                name="lastname"
                size="small"
                type="text"
                fullWidth
                inputProps={{
                  min: 1,
                }}
                value={currItem?.lastName || ''}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      lastName: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            {!currItem?.id ? (
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  required
                  name="password"
                  size="small"
                  type="text"
                  fullWidth
                  inputProps={{
                    min: 1,
                  }}
                  value={currItem?.password}
                  onChange={(e) => {
                    if (currItem) {
                      setCurrItem({
                        ...currItem,
                        password: e.currentTarget.value,
                      });
                    }
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <TextField
                label="Organization"
                required
                name="organization"
                size="small"
                type="text"
                fullWidth
                inputProps={{
                  min: 1,
                }}
                value={currItem?.organization || ''}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      organization: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Organization Role"
                name="organizationRole"
                size="small"
                type="text"
                fullWidth
                value={currItem?.organizationRole || ''}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      organizationRole: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone"
                required
                name="phone"
                size="small"
                type="text"
                fullWidth
                inputProps={{
                  min: 1,
                }}
                value={currItem?.phone || ''}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      phone: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            {currItem ? (
              <Grid item xs={12}>
                <UserRoleSelect
                  margin="dense"
                  value={currItem.role || EUserRoles.Editor}
                  onChange={(value) => {
                    if (currItem) {
                      setCurrItem({
                        ...currItem,
                        role: value || EUserRoles.Editor,
                      });
                    }
                  }}
                />
              </Grid>
            ) : null}
            
            {/* Newsletter Subscriptions */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 600 }}>
                Newsletter Subscriptions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currItem?.subscribe_investor || false}
                      onChange={(e) => {
                        if (currItem) {
                          setCurrItem({
                            ...currItem,
                            subscribe_investor: e.target.checked,
                          });
                        }
                      }}
                    />
                  }
                  label="Subscribe to Investor Newsletter"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currItem?.subscribe_startup || false}
                      onChange={(e) => {
                        if (currItem) {
                          setCurrItem({
                            ...currItem,
                            subscribe_startup: e.target.checked,
                          });
                        }
                      }}
                    />
                  }
                  label="Subscribe to Startup Newsletter"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currItem?.subscribe_researcher || false}
                      onChange={(e) => {
                        if (currItem) {
                          setCurrItem({
                            ...currItem,
                            subscribe_researcher: e.target.checked,
                          });
                        }
                      }}
                    />
                  }
                  label="Subscribe to Researcher Newsletter"
                />
              </Box>
            </Grid>
          </Grid>
          {/*         
          <TextField
            label="Index"
            size="small"
            margin="normal"
            type="number"
            inputProps={{
              min: 1,
            }}
            value={currItem?.priority ?? 1}
            onChange={(e) => {
              if (currItem) {
                setCurrItem({
                  ...currItem,
                  priority: +(e.currentTarget.value || 1),
                });
              }
            }}
          /> */}
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (!currItem) {
                return;
              }
              Promise.resolve()
                .then(() => {
                  setDeleting(true);
                })
                .then(() => {
                  if (currItem?.id) {
                    return userApi
                      .update(currItem.id, currItem)
                      .then(() => {
                        toast.success('Update successful');
                      })
                      .catch((e) => {
                        toast.error(e.message ?? e.toString());
                        throw e;
                      });
                  }
                  return userApi
                    .create(currItem)
                    .then(() => {
                      toast.success('Update successful');
                    })
                    .catch((e) => {
                      toast.error(e.message ?? e.toString());
                      throw e;
                    });
                })
                .then(() => {
                  loadData();
                })
                .finally(() => {
                  setCurrItem(undefined);
                  setDeleting(false);
                });
            }}
            color="primary"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : currItem?.id ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
